import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Settings, Upload, FileSpreadsheet, CheckCircle, XCircle, Clock, 
  AlertTriangle, RefreshCw, Users, ShieldCheck, Download, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AutolinePage = () => {
  const { user } = useOutletContext();
  const [loading, setLoading] = useState(true);
  
  // Settings
  const [settings, setSettings] = useState({
    enabled: false,
    channel_mapping: {
      canal_1: 'Newsletter',
      canal_2: 'Marketing',
      canal_3: 'Profiling',
      canal_4: 'Parceiros'
    }
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Import
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Pending consents
  const [pendingConsents, setPendingConsents] = useState([]);
  const [selectedPending, setSelectedPending] = useState([]);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSettings(),
        fetchPendingConsents()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/autoline/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchPendingConsents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/autoline/pending-consents`, { withCredentials: true });
      setPendingConsents(response.data || []);
    } catch (error) {
      console.error('Error fetching pending:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${BACKEND_URL}/api/autoline/settings`, settings, { withCredentials: true });
      toast.success('Definições guardadas com sucesso');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erro ao guardar definições');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Selecione um ficheiro');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/autoline/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      toast.success('Importação concluída com sucesso');
      setSelectedFile(null);
      document.getElementById('autoline-file-input').value = '';
      fetchPendingConsents();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar ficheiro');
    } finally {
      setImporting(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPending(pendingConsents.map(c => c.consent_id));
    } else {
      setSelectedPending([]);
    }
  };

  const handleSelectConsent = (consentId, checked) => {
    if (checked) {
      setSelectedPending(prev => [...prev, consentId]);
    } else {
      setSelectedPending(prev => prev.filter(id => id !== consentId));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedPending.length === 0) {
      toast.error('Selecione pelo menos um consentimento');
      return;
    }

    setValidating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/autoline/bulk-validate`,
        { consent_ids: selectedPending, action },
        { withCredentials: true }
      );
      
      toast.success(`${response.data.updated_count} consentimento(s) ${action === 'approve' ? 'aprovados' : 'rejeitados'}`);
      setSelectedPending([]);
      fetchPendingConsents();
    } catch (error) {
      console.error('Error bulk validating:', error);
      toast.error('Erro na validação em massa');
    } finally {
      setValidating(false);
    }
  };

  const handleSingleAction = async (consentId, action) => {
    setValidating(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/autoline/validate-consent/${consentId}?action=${action}`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Consentimento ${action === 'approve' ? 'aprovado' : 'rejeitado'}`);
      fetchPendingConsents();
    } catch (error) {
      console.error('Error validating:', error);
      toast.error('Erro ao validar consentimento');
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="autoline-page">
      <div>
        <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Validação RGPD Autoline
        </h1>
        <p className="text-slate-600">
          Importar e validar consentimentos do sistema Autoline
        </p>
      </div>

      {!settings.enabled && user?.role !== 'super_admin' && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Módulo Desativado</AlertTitle>
          <AlertDescription className="text-amber-700">
            O módulo Autoline está desativado. Ative nas definições para utilizar a funcionalidade de importação.
          </AlertDescription>
        </Alert>
      )}

      {!settings.enabled && user?.role === 'super_admin' && (
        <Alert className="border-blue-300 bg-blue-50">
          <ShieldCheck className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">Modo Super Admin</AlertTitle>
          <AlertDescription className="text-blue-700">
            Como super admin, tem acesso total ao módulo Autoline independentemente das configurações do tenant.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="import" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="import" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Importar
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pendentes
            {pendingConsents.length > 0 && (
              <Badge className="ml-2 bg-amber-500">{pendingConsents.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Definições
          </TabsTrigger>
        </TabsList>

        {/* Import Tab */}
        <TabsContent value="import">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-[#10B981]" />
                  Importar Ficheiro Autoline
                </CardTitle>
                <CardDescription>
                  Carregue o ficheiro Excel exportado do Autoline para validação RGPD
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="autoline-file-input">Ficheiro Excel (.xlsx)</Label>
                  <Input
                    id="autoline-file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    disabled={!settings.enabled}
                    className="mt-2"
                  />
                  {selectedFile && (
                    <p className="text-sm text-slate-600 mt-2">
                      Ficheiro: <span className="font-medium">{selectedFile.name}</span>
                    </p>
                  )}
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Colunas Esperadas:</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Nº Cliente (autoline_id)</li>
                    <li>• Nome Cliente</li>
                    <li>• Telefóvel (telemóvel)</li>
                    <li>• E-mail</li>
                    <li>• Descrição Acordo</li>
                    <li>• Data Criação Utilizador</li>
                    <li>• Canal 1, 2, 3, 4 (Aprovado/Desconhecido/Recusado)</li>
                  </ul>
                </div>

                <Button
                  onClick={handleImport}
                  disabled={!selectedFile || importing || !settings.enabled}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A importar...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Importar e Validar
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results Card */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-[#3B82F6]" />
                  Resultado da Importação
                </CardTitle>
              </CardHeader>
              <CardContent>
                {importResult ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-[#0F172A]">{importResult.total_records}</div>
                        <p className="text-sm text-slate-600">Registos Processados</p>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-emerald-600">{importResult.created_titulares}</div>
                        <p className="text-sm text-slate-600">Titulares Criados</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-600">{importResult.updated_titulares}</div>
                        <p className="text-sm text-slate-600">Titulares Atualizados</p>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-emerald-600">{importResult.active_consents}</div>
                        <p className="text-sm text-slate-600">Cons. Ativos</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-amber-600">{importResult.pending_consents}</div>
                        <p className="text-sm text-slate-600">Cons. Pendentes</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-red-600">{importResult.revoked_consents}</div>
                        <p className="text-sm text-slate-600">Cons. Revogados</p>
                      </div>
                    </div>
                    
                    {importResult.errors?.length > 0 && (
                      <Alert className="border-amber-300 bg-amber-50">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertTitle className="text-amber-800">Avisos</AlertTitle>
                        <AlertDescription className="text-amber-700">
                          <ul className="list-disc ml-4 mt-2">
                            {importResult.errors.slice(0, 5).map((err, idx) => (
                              <li key={idx}>{err}</li>
                            ))}
                            {importResult.errors.length > 5 && (
                              <li>... e mais {importResult.errors.length - 5} avisos</li>
                            )}
                          </ul>
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Importe um ficheiro para ver os resultados</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    Consentimentos Pendentes
                  </CardTitle>
                  <CardDescription>
                    Validar consentimentos com status "Desconhecido" do Autoline
                  </CardDescription>
                </div>
                {selectedPending.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleBulkAction('approve')}
                      disabled={validating}
                      className="bg-[#10B981] hover:bg-[#10B981]/90"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aprovar ({selectedPending.length})
                    </Button>
                    <Button
                      onClick={() => handleBulkAction('reject')}
                      disabled={validating}
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Rejeitar ({selectedPending.length})
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {pendingConsents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
                  <p>Sem consentimentos pendentes</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedPending.length === pendingConsents.length}
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Titular</TableHead>
                      <TableHead>Autoline ID</TableHead>
                      <TableHead>Contacto</TableHead>
                      <TableHead>Finalidade</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingConsents.map((consent) => (
                      <TableRow key={consent.consent_id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedPending.includes(consent.consent_id)}
                            onCheckedChange={(checked) => handleSelectConsent(consent.consent_id, checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{consent.titular_name}</p>
                            <p className="text-sm text-slate-500">{consent.titular_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{consent.autoline_id || '-'}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{consent.contact_value}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {consent.purposes?.map(p => (
                              <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-blue-50 text-blue-700">{consent.consent_origin || 'Autoline'}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSingleAction(consent.consent_id, 'approve')}
                              disabled={validating}
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSingleAction(consent.consent_id, 'reject')}
                              disabled={validating}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#3B82F6]" />
                Configuração do Módulo Autoline
              </CardTitle>
              <CardDescription>
                Configurar o mapeamento de canais e ativar/desativar o módulo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable Module */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Ativar Módulo Autoline</Label>
                  <p className="text-sm text-slate-500">
                    Quando ativo, permite importar e validar consentimentos do Autoline
                  </p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                />
              </div>

              {/* Channel Mapping */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Mapeamento de Canais</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Defina qual a finalidade de consentimento para cada canal do Autoline
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="canal-1">Canal 1</Label>
                    <Input
                      id="canal-1"
                      value={settings.channel_mapping?.canal_1 || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        channel_mapping: { ...prev.channel_mapping, canal_1: e.target.value }
                      }))}
                      placeholder="Ex: Newsletter"
                    />
                  </div>
                  <div>
                    <Label htmlFor="canal-2">Canal 2</Label>
                    <Input
                      id="canal-2"
                      value={settings.channel_mapping?.canal_2 || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        channel_mapping: { ...prev.channel_mapping, canal_2: e.target.value }
                      }))}
                      placeholder="Ex: Marketing"
                    />
                  </div>
                  <div>
                    <Label htmlFor="canal-3">Canal 3</Label>
                    <Input
                      id="canal-3"
                      value={settings.channel_mapping?.canal_3 || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        channel_mapping: { ...prev.channel_mapping, canal_3: e.target.value }
                      }))}
                      placeholder="Ex: Profiling"
                    />
                  </div>
                  <div>
                    <Label htmlFor="canal-4">Canal 4</Label>
                    <Input
                      id="canal-4"
                      value={settings.channel_mapping?.canal_4 || ''}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        channel_mapping: { ...prev.channel_mapping, canal_4: e.target.value }
                      }))}
                      placeholder="Ex: Parceiros"
                    />
                  </div>
                </div>
              </div>

              {/* Status Mapping Info */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Mapeamento de Estados</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>Aprovado</strong> → Consentimento Ativo (origem: Externo Autoline)</li>
                  <li>• <strong>Desconhecido</strong> → Consentimento Pendente (requer validação)</li>
                  <li>• <strong>Recusado</strong> → Consentimento Revogado</li>
                </ul>
              </div>

              <Button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="bg-[#0F172A] hover:bg-[#0F172A]/90"
              >
                {savingSettings ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A guardar...
                  </>
                ) : (
                  'Guardar Definições'
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AutolinePage;
